import React from "react";
import { listStockItems } from "../../data/stockItems";
import {
  BAG_COLOURS,
  BAG_LEVELS,
  BAG_MIN,
  bagsTotal,
  buildTopUpLines,
  HAT_COLOURS,
  HAT_MIN,
  HAT_SIZES,
  hatsTotal,
  OTHER_ITEMS,
  PACK_MAX,
  PACK_YEARS,
  REIMBURSEMENT_AMOUNT,
  SECTION_LABELS,
  SECTION_ORDER,
  SOCK_MIN,
  SOCK_SIZES,
  socksTotal,
  topUpHasAnything,
  validateTopUp,
} from "../../services/stockTopUpForm";
import { GLYPH } from "../../utils/glyphs";
import { Btn, Card } from "../../utils/ui.jsx";

// Stock Top-up capture (stock-top-ups plan §2). The service turns on the sections it
// needs from the toggle bar at the top; only enabled sections show + submit. Self-
// contained + ISOLATED — the whole form lives in the wizard's lifted state (ephemeral
// staging), validates on submit, builds the DB lines, and hands them to onSubmit.
const inputStyle = {
  width: 52,
  padding: "4px 6px",
  border: "1px solid #CFE0DE",
  borderRadius: 6,
  fontSize: 13,
  textAlign: "center",
};
const selectStyle = {
  padding: "4px 8px",
  border: "1px solid #CFE0DE",
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "inherit",
  background: "#fff",
};
const th = {
  fontSize: 12,
  color: "#607D79",
  fontWeight: 700,
  padding: "6px 8px",
  textAlign: "center",
};
const td = { padding: "6px 8px", borderTop: "1px solid #EEF3F2" };
const sectionTitle = { fontWeight: 700, fontSize: 15, color: "#1A2E2B", marginBottom: 4 };
const hint = { fontSize: 12, color: "#607D79", marginBottom: 10 };
const totalBox = {
  fontSize: 13,
  color: "#1A2E2B",
  fontWeight: 700,
  border: "1px solid #CFE0DE",
  borderRadius: 6,
  padding: "6px 12px",
};

// "Required" dropdown option sets (John's feedback). 0 = none (stored blank).
function range0(n) {
  const a = [];
  for (let i = 0; i <= n; i++) a.push(i);
  return a;
}
const PACK_REQ_OPTS = range0(PACK_MAX); // 0..3
const BAG_REQ_OPTS = [0, 10, 20, 30]; // boxes of 10
const HAT_REQ_OPTS = range0(10); // 0..10
const SOCK_REQ_OPTS = [0, 6, 12, 18]; // packs (each pack = 2 pairs); ordered in lots of 6

export default function StockTopUpStep({
  state,
  setState,
  onBack,
  onSubmit,
  submitLabel = "Submit Top-up",
  submittingLabel = "Submitting…",
  // Read-only walkthrough (Phase 5): the service REVIEWS a staff-prepared top-up and can
  // only Submit it — every quantity / section / note control is locked, so the submitted
  // order is guaranteed identical to what staff prepared. Default false (normal capture).
  readOnly = false,
  // Require an "On hand" figure for every ordered item (John, 5 Sep 2026). ON for a DFV
  // Service creating its own top-up; OFF for a staff-prepared prefill (default).
  requireOnHand = false,
}) {
  const [stockItems, setStockItems] = React.useState([]);
  const [errors, setErrors] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitErr, setSubmitErr] = React.useState("");

  React.useEffect(function () {
    let alive = true;
    listStockItems().then(function (rows) {
      if (alive) setStockItems(rows);
    });
    return function () {
      alive = false;
    };
  }, []);

  // Ephemeral staging: warn before leaving/refresh while the form has content.
  const dirty = topUpHasAnything(state) || state.note.trim() !== "";
  React.useEffect(
    function () {
      function onBeforeUnload(e) {
        if (dirty) {
          e.preventDefault();
          e.returnValue = "";
        }
      }
      window.addEventListener("beforeunload", onBeforeUnload);
      return function () {
        window.removeEventListener("beforeunload", onBeforeUnload);
      };
    },
    [dirty]
  );

  function toggleSection(section) {
    setState(function (prev) {
      return {
        ...prev,
        enabledSections: { ...prev.enabledSections, [section]: !prev.enabledSections[section] },
      };
    });
  }

  function setCell(section, key, field, value) {
    const v = String(value).replace(/[^0-9]/g, "");
    setState(function (prev) {
      const sec = { ...(prev[section] || {}) };
      const cell = { ...(sec[key] || { onHand: "", required: "" }) };
      cell[field] = v;
      sec[key] = cell;
      return { ...prev, [section]: sec };
    });
  }
  function cellVal(section, key, field) {
    return state[section]?.[key]?.[field] ?? "";
  }
  function reqNum(section, key) {
    return Number.parseInt(cellVal(section, key, "required") || "", 10) || 0;
  }

  async function handleSubmit() {
    const errs = validateTopUp(state, stockItems, { requireOnHand });
    setErrors(errs);
    if (errs.length > 0) {
      window.scrollTo(0, 0);
      return;
    }
    setSubmitErr("");
    setSubmitting(true);
    const lines = buildTopUpLines(state, stockItems);
    const res = await onSubmit(lines, state.note.trim());
    if (res?.error) {
      setSubmitErr(res.error);
      setSubmitting(false);
    }
    // On success the parent navigates away.
  }

  // ── small render helpers (plain functions, NOT components — so inputs keep focus) ──
  function numInput(section, key, field, ariaLabel) {
    return (
      <input
        type="number"
        min="0"
        inputMode="numeric"
        aria-label={ariaLabel}
        value={cellVal(section, key, field)}
        onChange={(e) => setCell(section, key, field, e.target.value)}
        disabled={readOnly}
        style={inputStyle}
      />
    );
  }
  // A "Required" dropdown from an explicit option list (0 stored as blank).
  function reqSelect(section, key, opts, ariaLabel) {
    return (
      <select
        aria-label={ariaLabel}
        value={cellVal(section, key, "required")}
        onChange={(e) => setCell(section, key, "required", e.target.value)}
        disabled={readOnly}
        style={selectStyle}
      >
        {opts.map((o) => (
          <option key={o} value={o === 0 ? "" : String(o)}>
            {o}
          </option>
        ))}
      </select>
    );
  }
  // An On-hand (input) / Required (dropdown) pair for the matrix cells — the label
  // sits ABOVE its control so the full "On Hand" fits without cramping.
  function qtyPair(section, key, reqOpts) {
    return (
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "center",
          alignItems: "flex-end",
          padding: "0 16px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            fontSize: 10,
            color: "#8AA5A1",
          }}
        >
          On Hand
          {numInput(section, key, "onHand", `${key} on hand`)}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            fontSize: 10,
            color: "#1A2E2B",
          }}
        >
          Req
          {reqSelect(section, key, reqOpts, `${key} required`)}
        </div>
      </div>
    );
  }

  const on = state.enabledSections || {};
  const bt = bagsTotal(state);
  const ht = hatsTotal(state);
  const st = socksTotal(state);
  const bagsPrimary = BAG_COLOURS.reduce((sum, c) => sum + reqNum("bags", `${c}|Primary`), 0);
  const bagsHigh = BAG_COLOURS.reduce((sum, c) => sum + reqNum("bags", `${c}|High`), 0);
  function totalNote(total, min, unit) {
    const ok = total === 0 || total >= min;
    return (
      <span style={{ fontSize: 12, color: ok ? "#607D79" : "#C62828", fontWeight: 600 }}>
        Total: {total} {unit} (minimum {min} if ordering)
      </span>
    );
  }

  return (
    <div>
      {errors.length > 0 && (
        <Card style={{ marginBottom: 16, border: "1px solid #E57373", background: "#FFF5F5" }}>
          <div style={{ fontWeight: 700, color: "#C62828", marginBottom: 6 }}>
            Please fix the following:
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, color: "#C62828", fontSize: 13 }}>
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Section toggles — the service turns on only the sections it needs. */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ ...hint, marginBottom: 8 }}>
          {readOnly ? "Sections in this top-up:" : "Turn on the sections you need:"}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {SECTION_ORDER.map((s) => {
            const isOn = !!on[s];
            return (
              <label
                key={s}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: isOn ? "#E0F2F1" : "#FFFFFF",
                  border: `1px solid ${isOn ? "#00897B" : "#CFE0DE"}`,
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: readOnly ? "default" : "pointer",
                  color: isOn ? "#00695C" : "#1A2E2B",
                }}
              >
                <input
                  type="checkbox"
                  checked={isOn}
                  onChange={() => toggleSection(s)}
                  disabled={readOnly}
                  style={{ accentColor: "#00897B" }}
                />
                {SECTION_LABELS[s]}
              </label>
            );
          })}
        </div>
      </Card>

      {!SECTION_ORDER.some((s) => on[s]) && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: "#8AA5A1" }}>
            Select the sections you need above to start your top-up.
          </div>
        </Card>
      )}

      {/* 1 — Stationery Packs */}
      {on.packs && (
        <Card style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Stationery Packs</div>
          <div style={hint}>Per year level. Maximum {PACK_MAX} per level.</div>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Year level</th>
                <th style={th}>On hand</th>
                <th style={th}>Required</th>
              </tr>
            </thead>
            <tbody>
              {PACK_YEARS.map((y) => (
                <tr key={y}>
                  <td style={{ ...td, fontSize: 13 }}>{y}</td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {numInput("packs", y, "onHand", `${y} on hand`)}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {reqSelect("packs", y, PACK_REQ_OPTS, `${y} required`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* 2 — Bulk Stationery (admin-editable catalogue) */}
      {on.bulk && (
        <Card style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Bulk Stationery</div>
          <div style={hint}>Ordered in packs. Each item has a maximum number of packs.</div>
          {stockItems.length === 0 ? (
            <div style={{ fontSize: 13, color: "#8AA5A1" }}>No bulk catalogue items available.</div>
          ) : (
            <table style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: "left" }}>Item</th>
                  <th style={th}>Pack size</th>
                  <th style={th}>Max packs</th>
                  <th style={th}>On hand</th>
                  <th style={th}>Required</th>
                </tr>
              </thead>
              <tbody>
                {stockItems.map((it) => (
                  <tr key={it.id}>
                    <td style={{ ...td, fontSize: 13 }}>{it.name}</td>
                    <td style={{ ...td, textAlign: "center", fontSize: 13, color: "#607D79" }}>
                      {it.pack_size}
                    </td>
                    <td style={{ ...td, textAlign: "center", fontSize: 13, color: "#607D79" }}>
                      {it.max_qty}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {numInput("bulk", it.id, "onHand", `${it.name} on hand`)}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {reqSelect("bulk", it.id, range0(it.max_qty), `${it.name} required`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* 3 — School Bags */}
      {on.bags && (
        <Card style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>School Bags</div>
          <div style={hint}>
            Ordered in boxes of 10, mixable across levels. {totalNote(bt, BAG_MIN, "bags")}
          </div>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Colour</th>
                {BAG_LEVELS.map((lv) => (
                  <th key={lv} style={th}>
                    {lv}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BAG_COLOURS.map((colour) => (
                <tr key={colour}>
                  <td style={{ ...td, fontSize: 13 }}>{colour}</td>
                  {BAG_LEVELS.map((lv) => (
                    <td key={lv} style={td}>
                      {qtyPair("bags", `${colour}|${lv}`, BAG_REQ_OPTS)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", gap: 16, marginTop: 12, justifyContent: "flex-end" }}>
            <div style={totalBox}>Total Primary: {bagsPrimary}</div>
            <div style={totalBox}>Total High: {bagsHigh}</div>
          </div>
        </Card>
      )}

      {/* 4 — Other Stock (either/or) */}
      {on.other && (
        <Card style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Other Stock</div>
          <div style={{ marginBottom: 10, display: "flex", gap: 18, fontSize: 13 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="otherMode"
                checked={state.otherMode === "quantities"}
                onChange={() => setState((p) => ({ ...p, otherMode: "quantities" }))}
                disabled={readOnly}
              />
              Order quantities
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="radio"
                name="otherMode"
                checked={state.otherMode === "reimbursement"}
                onChange={() => setState((p) => ({ ...p, otherMode: "reimbursement" }))}
                disabled={readOnly}
              />
              ${REIMBURSEMENT_AMOUNT} reimbursement instead
            </label>
          </div>
          {state.otherMode === "quantities" ? (
            <table style={{ borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ ...th, textAlign: "left" }}>Item</th>
                  <th style={th}>On hand</th>
                  <th style={th}>Required</th>
                </tr>
              </thead>
              <tbody>
                {OTHER_ITEMS.map((it) => (
                  <tr key={it.key}>
                    <td style={{ ...td, fontSize: 13 }}>{it.label}</td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {numInput("other", it.key, "onHand", `${it.label} on hand`)}
                    </td>
                    <td style={{ ...td, textAlign: "center" }}>
                      {numInput("other", it.key, "required", `${it.label} required`)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ fontSize: 13, color: "#1A2E2B" }}>
              A ${REIMBURSEMENT_AMOUNT} reimbursement will be requested in place of Lunch Boxes and
              Water Bottles.
            </div>
          )}
        </Card>
      )}

      {/* 5 — Hats */}
      {on.hats && (
        <Card style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Hats</div>
          <div style={hint}>{totalNote(ht, HAT_MIN, "hats")}</div>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Colour</th>
                {HAT_SIZES.map((sz) => (
                  <th key={sz} style={th}>
                    {sz}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HAT_COLOURS.map((colour) => (
                <tr key={colour}>
                  <td style={{ ...td, fontSize: 13 }}>{colour}</td>
                  {HAT_SIZES.map((sz) => (
                    <td key={sz} style={td}>
                      {qtyPair("hats", `${colour}|${sz}`, HAT_REQ_OPTS)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", marginTop: 12, justifyContent: "flex-end" }}>
            <div style={totalBox}>Total Hats: {ht}</div>
          </div>
        </Card>
      )}

      {/* 6 — Socks */}
      {on.socks && (
        <Card style={{ marginBottom: 16 }}>
          <div style={sectionTitle}>Socks</div>
          <div style={hint}>Each pack = 2 pairs. {totalNote(st, SOCK_MIN, "packs")}</div>
          <table style={{ borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: "left" }}>Size</th>
                <th style={th}>On hand</th>
                <th style={th}>Required (packs)</th>
              </tr>
            </thead>
            <tbody>
              {SOCK_SIZES.map((sz) => (
                <tr key={sz}>
                  <td style={{ ...td, fontSize: 13 }}>{sz}</td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {numInput("socks", sz, "onHand", `${sz} on hand`)}
                  </td>
                  <td style={{ ...td, textAlign: "center" }}>
                    {reqSelect("socks", sz, SOCK_REQ_OPTS, `${sz} required`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: "flex", marginTop: 12, justifyContent: "flex-end" }}>
            <div style={totalBox}>Total Packs of 2 Pair: {st}</div>
          </div>
        </Card>
      )}

      {/* Notes */}
      <Card style={{ marginBottom: 16 }}>
        <div style={sectionTitle}>Other / Notes</div>
        <textarea
          value={state.note}
          onChange={(e) => setState((p) => ({ ...p, note: e.target.value }))}
          readOnly={readOnly}
          rows={3}
          style={{
            width: "100%",
            padding: "8px 10px",
            border: "1px solid #CFE0DE",
            borderRadius: 6,
            fontSize: 13,
            fontFamily: "inherit",
            resize: "vertical",
          }}
          placeholder="Anything else about this top-up request…"
        />
      </Card>

      {submitErr && (
        <div style={{ color: "#C62828", fontSize: 13, marginBottom: 10 }}>{submitErr}</div>
      )}

      <div
        style={{
          position: "sticky",
          bottom: 0,
          background: "white",
          borderTop: "1px solid #CFE0DE",
          padding: "12px 20px",
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          zIndex: 10,
          borderRadius: "0 0 10px 10px",
        }}
      >
        <Btn variant="secondary" onClick={onBack} disabled={submitting}>
          {GLYPH.arrowLeft} Back
        </Btn>
        {/* Set shelter expectations: unlike a Fit-out request, a Stock Top-up has no
            Save Draft & Exit — it must be submitted in one go (ephemeral staging). */}
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: "#5A6B69",
            border: "1px solid #CFE0DE",
            borderRadius: 8,
            padding: "8px 14px",
          }}
        >
          Stock Top-ups cannot be saved as a Draft
        </span>
        <Btn
          onClick={handleSubmit}
          disabled={submitting}
          style={{ padding: "11px 32px", fontSize: 16 }}
        >
          {submitting ? submittingLabel : submitLabel}
        </Btn>
      </div>
    </div>
  );
}
